import { type SxProps, type Theme } from '@mui/material'

type AllowedPrimitives = false | null | undefined

/**
 * Merges sx, later arguments take precedence over previous arguments.
 * SxProps as arrays can be nested arbitrarily deep.
 *
 * Rationale: `mergeSx` is designed to reduce boilerplate in merging `sx` props.
 * `sx={{ sxDefault, ...sx }}` doesn't cut it unless it's sure to be an object.
 * `SxProps` is a complex type, it can be an array, object, or function:
 *
 * ```
 * export type SxProps<Theme extends object = {}> =
 *  | SystemStyleObject<Theme>
 *  | ((theme: Theme) => SystemStyleObject<Theme>)
 *  | ReadonlyArray<
 *      boolean | SystemStyleObject<Theme> | ((theme: Theme) => SystemStyleObject<Theme>)
 *    >;
 * ```
 *
 * See https://v5.mui.com/system/getting-started/the-sx-prop/#passing-the-sx-prop
 * for more info.
 *
 * See our docs on [MUI](./docs/material-ui.md) for more on best practices.
 */
export function mergeSx<T extends Theme>(
  ...sxProps: (SxProps<T> | AllowedPrimitives)[]
) {
  return sxProps.flat(Infinity).filter(Boolean) as SxProps<T>
}
